/* HenaketLoader - the canonical Henaket dots loader.
 *
 * 0.42.40 - Eight dots in a ring; one dark, fading to four pale ones;
 * the ring steps round once a second. Built to the Henaket loader build
 * specification, which restates the Gender Dashboard's HenaketLoader.tsx.
 *
 * Do not recolour it, change the dot count, or swap in another spinner:
 * the spec says a variant must be added to the design system first.
 */

import { useEffect, useRef } from 'react';
import styles from './HenaketLoader.module.css';

/* Section 3. Drawn on a 64x64 viewBox, centred at (32,32), nominal
 * radius 26, every dot r=5, numbered clockwise from the top.
 *
 * The diagonals use ROUNDED coordinates (50/14), not exact trigonometry.
 * Kept as given: the spec says explicitly not to "correct" them.
 *
 * Fixed hex values, not theme tokens, and unaffected by the primary
 * colour. No stroke, shadow or opacity - the fade is the five greys. */
const DOTS = [
  { cx: 32, cy: 6, fill: '#4B4B4B' },   // 1 top
  { cx: 50, cy: 14, fill: '#818181' },  // 2 top right
  { cx: 58, cy: 32, fill: '#A7A7A7' },  // 3 right
  { cx: 50, cy: 50, fill: '#CACACA' },  // 4 bottom right
  { cx: 32, cy: 58, fill: '#DADADA' },  // 5 bottom
  { cx: 14, cy: 50, fill: '#DADADA' },  // 6 bottom left
  { cx: 6, cy: 32, fill: '#DADADA' },   // 7 left
  { cx: 14, cy: 14, fill: '#DADADA' },  // 8 top left
];

/* The sizes: 64 (default, with a label), 40, and 24 for inline use, as
 * the spec defines them; 0.43.71 adds 128 and 256 for a large, page-level
 * loader, at the Henaket owner's request (the drawing is a 64-unit
 * viewBox, so it scales exactly; the spec should list them too).
 * Anything else falls back to 64. Listed smallest first for the editor's
 * dropdown; the default stays 64. */
export const LOADER_SIZES = [24, 40, 64, 128, 256];

/* 0.42.41 - `still`: the ring does not turn.
 *
 * For AUTHORING surfaces - the form editor's canvas - where a loader
 * spinning forever beside the element being edited is a distraction, and
 * reads as the editor itself being busy. It is not a new variant: the
 * spec already draws this exact still ring for reduced motion. Citizens
 * never see it; the published page always animates. */
export default function HenaketLoader({ size = 64, label = '', ariaLabel = '', still = false }) {
  const px = LOADER_SIZES.includes(Number(size)) ? Number(size) : 64;
  const text = typeof label === 'string' ? label.trim() : '';
  /* Section 6: role=status so it is announced politely without moving
     focus; the accessible name is the label, or - with no label shown -
     the caller's fallback, else "Loading". */
  const name = text || (ariaLabel || '').trim() || 'Loading';

  /* 0.43.77 - The ring is turned by a script, not a CSS animation.
   * WebKit (every iPhone browser, Chrome included) stepped a CSS
   * animation once (0.43.75) or not at all (0.43.76). Setting the SVG's
   * own rotate attribute every 125 ms is the plainest SVG there is and
   * behaves the same in every browser: eight 45-degree ticks a second,
   * as the spec requires. Still when `still` is set or the viewer asks
   * for reduced motion; paused while the tab is hidden. One attribute
   * write per tick, no React re-render. */
  const ringRef = useRef(null);
  useEffect(() => {
    const g = ringRef.current;
    if (!g) return undefined;
    const mq = typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    let step = 0;
    let timer = null;
    const draw = () => g.setAttribute('transform', `rotate(${step * 45} 32 32)`);
    const stop = () => { if (timer) { clearInterval(timer); timer = null; } };
    const start = () => {
      stop();
      if (still || (mq && mq.matches) || document.hidden) { step = 0; draw(); return; }
      timer = setInterval(() => { step = (step + 1) % 8; draw(); }, 125);
    };
    start();
    const onVis = () => start();
    document.addEventListener('visibilitychange', onVis);
    if (mq && mq.addEventListener) mq.addEventListener('change', onVis);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVis);
      if (mq && mq.removeEventListener) mq.removeEventListener('change', onVis);
    };
  }, [still]);
  return (
    <div className={styles.loader} role="status" aria-label={name}>
      {/* 0.43.76 - The ring turns on a wrapper, not on the <svg> itself:
          WebKit (every iPhone browser, Chrome included) stopped an
          animated transform on the svg root after one step. The dots
          do not move relative to each other, so it looks the same. */}
      <svg
        className={styles.ring}
        width={px}
        height={px}
        viewBox="0 0 64 64"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <g ref={ringRef}>
          {DOTS.map((d, i) => (
            <circle key={i} cx={d.cx} cy={d.cy} r={5} fill={d.fill} />
          ))}
        </g>
      </svg>
      {text ? <p className={styles.label}>{text}</p> : null}
    </div>
  );
}
